<template>
  <div :class="{ 'message-div': isDocumentSigned && signingType === 'local' }">
    <v-fade-transition v-if="signingType === 'local'">
      <v-layout
        v-if="!isDocumentSigned"
        column
        d-flex
      >
        <embed
          :src="pdfUrl"
          type="application/pdf"
          class="pdf-embed"
          width="100%"
          height="100%"
        >
        <div class="text-xs-center ma-2">
          <v-btn
            :loading="isRequestPending"
            :disabled="isRequestPending"
            color="primary"
            style="width: 150px"
            @click="getDocumentSignTokens"
          >
            {{ $t('sign') }}
          </v-btn>
        </div>
      </v-layout>
      <div
        v-else
        class="text-xs-center"
      >
        {{ $t('document_signed') }}
      </div>
    </v-fade-transition>

    <div v-else-if="errorMessage" class="pa-3 text-xs-center">
      <h2 class="headline">
        {{ errorMessage }}
      </h2>
    </div>
    <div v-else-if="signingType === 'isign' && !isSigningExpired">
      <div v-if="signUrl" class="pa-3 text-xs-right">
        {{ $t('time_left_to_sign') }}: {{ timerLabel }}
      </div>
      <div v-if="isDocumentLocked" class="pa-3 floating-message text-xs-right">
        {{ $t('document_is_locked') }}<br>
        ({{ $t('it_usually_takes_around_5_minutes') }})
      </div>
      <SignDocument
        :dialog="true"
        :wrapper-classes="'pa-0 iframe-wrapper'"
        :iframe-id="'isign-gateway'"
        :sign-url="signUrl"
        :checking-signature="checkingSignature"
        hide-actions
      />
    </div>
    <div v-else class="pa-3 text-xs-center">
      <h2 class="subheading">
        {{ $t('time_to_sign_expiration_message') }}
      </h2>
      <v-btn
        color="primary"
        class="mt-3"
        @click="getDocumentSignTokens"
      >
        {{ $t('initiate_signing') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import SignDocument from '../components/SignDocument.vue';

export default {
  name: 'SignSentDocument',

  components: {
    SignDocument,
  },

  data() {
    return {
      secondsLeft: null,
      timerLabel: '',

      errorMessage: '',
      signUrl: '',
      pdfUrl: '',
      signingType: '', // isign, local
      checkingSignature: false,
      isRequestPending: false,
      isDocumentLocked: false,
      isSigningExpired: false,
      isDocumentSigned: false, // used for locally signed documents
    };
  },

  created() {
    const { id, hash, type } = this.$route.params;
    this.signingType = type;

    if (type === 'local') {
      this.pdfUrl = `${process.env.VUE_APP_API_ENDPOINT}/documents/${id}/${hash}`;
    } else {
      this.getDocumentSignTokens();
    }
  },

  methods: {
    ...mapActions({
      getSigningTokens: 'documents/getSigningTokens',
    }),

    setTimer() {
      this.secondsLeft = 300;
      const setTimerLabel = () => {
        this.secondsLeft -= 1;
        let label = `0${Math.floor(this.secondsLeft / 60)}:`;
        const seconds = this.secondsLeft % 60;
        if (seconds < 10) {
          label += '0';
        }
        label += seconds;
        this.timerLabel = label;
      };
      setTimerLabel();
      const intervalId = setInterval(() => {
        if (this.secondsLeft <= 0) {
          clearInterval(intervalId);
          this.isSigningExpired = true;
        } else {
          setTimerLabel();
        }
      }, 1000);
    },

    getDocumentSignTokens() {
      this.isSigningExpired = false;
      this.signUrl = '';
      const { id, hash } = this.$route.params;
      const payload = {
        id,
        hash,
      };
      this.isRequestPending = true;
      this.getSigningTokens(payload).then((res) => {
        this.isDocumentLocked = false;
        if (res.data.token) {
          this.setTimer();
          this.signUrl = `${process.env.VUE_APP_DOKOBIT_SIGNING_URL}/signing/${res.data.token}?access_token=${res.data.signer_token}`;
        } else {
          this.isDocumentSigned = true;
        }
        this.isRequestPending = false;
      }).catch((err) => {
        this.isRequestPending = false;
        if (!err.response) {
          this.errorMessage = this.$t('failed_to_initiate_document_signing');
          return;
        }
        if (err.response.status === 409) {
          // Document is currently being signed by someone else.
          // Wait 5 seconds, then try to fetch signing tokens again.
          this.isDocumentLocked = true;
          setTimeout(() => {
            this.getDocumentSignTokens();
          }, 5000);
        } else if (err.response.status === 404) {
          this.errorMessage = this.$t('document_not_found');
        } else if (err.response.status === 400) {
          this.errorMessage = this.$t('wrong_document_link');
        } else {
          this.errorMessage = this.$t('failed_to_initiate_document_signing');
        }
      });
    },
  },
};
</script>

<style scoped>
  .pdf-embed {
    height: calc(100vh - 64px);
  }
</style>
